export const FUNDS = ["G", "F", "C", "S", "I", "L"];
export const L_FUNDS = ["L Income", "L 2030", "L 2035", "L 2040", "L 2045", "L 2050", "L 2055", "L 2060", "L 2065", "L 2070", "L 2075"];
export const ALL_TSP_FUNDS = ["G", "F", "C", "S", "I", ...L_FUNDS];

export const FUND_META = {
  G: { name: "G Fund", desc: "Government Securities", color: "#94A3B8" },
  F: { name: "F Fund", desc: "Fixed Income Index", color: "#818CF8" },
  C: { name: "C Fund", desc: "Common Stock (S&P 500)", color: "#3B82F6" },
  S: { name: "S Fund", desc: "Small Cap (DJ Completion)", color: "#F59E0B" },
  I: { name: "I Fund", desc: "International (ACWI ex-US)", color: "#22D3EE" },
  L: { name: "L Funds", desc: "Lifecycle", color: "#059669" },
  External: { name: "IRA / External", desc: "Tickers", color: "#F43F5E" },
};
export const fundColor = (k) => FUND_META[k?.startsWith("L ") ? "L" : k]?.color || "#64748B";

// Approximate long-run annualized returns since inception (illustrative, not guaranteed).
export const FUND_CAGR = { G: 4.4, F: 5.2, C: 10.4, S: 9.3, I: 5.4, L: 7.4 };

export const LIMITS = { year: 2026, elective: 24500, catchup: 8000, superCatchup: 11250, ira: 7500, iraCatchup: 1100 };
export const PERIODS = { biweekly: 26, semimonthly: 24, monthly: 12 };

export const tspLimit = (age) =>
  LIMITS.elective + (age >= 60 && age <= 63 ? LIMITS.superCatchup : age >= 50 ? LIMITS.catchup : 0);
export const iraLimit = (age) => LIMITS.ira + (age >= 50 ? LIMITS.iraCatchup : 0);

export function blendedReturn(alloc) {
  const total = FUNDS.reduce((s, f) => s + (Number(alloc?.[f]) || 0), 0) || 1;
  return FUNDS.reduce((s, f) => s + ((Number(alloc?.[f]) || 0) / total) * FUND_CAGR[f], 0);
}

export function employeePct(p, salary) {
  if (p.contrib_mode !== "fixed") return Number(p.contrib_value) || 0;
  return salary > 0 ? ((Number(p.contrib_value) || 0) * PERIODS[p.pay_frequency || "biweekly"]) / salary * 100 : 0;
}

export const contribOf = (p) => ({ mode: p.contrib_mode === "fixed" ? "fixed" : "percent", value: Number(p.contrib_value) || 0 });
export const withContrib = (p, c) => ({ ...p, contrib_mode: c.mode, contrib_value: c.value });
export const perPaycheck = (p, pct) => ((Number(p.salary) || 0) * pct) / 100 / PERIODS[p.pay_frequency || "biweekly"];
export const perPaycheckLimit = (p) => tspLimit(Number(p.age) || 0) / PERIODS[p.pay_frequency || "biweekly"];

export function convertContrib(p, c, mode) {
  if (c.mode === mode) return c;
  const pct = employeePct(withContrib(p, c), Number(p.salary) || 0);
  return mode === "fixed" ? { mode, value: Math.round(perPaycheck(p, pct)) } : { mode, value: Math.round(pct * 10) / 10 };
}

export function matchPct(system, empPct) {
  if (system !== "FERS" && system !== "BRS") return 0;
  return 1 + Math.min(empPct, 3) + Math.max(0, Math.min(empPct, 5) - 3) * 0.5;
}

export function annualContribs(p, salary, age, overridePct) {
  const ep = overridePct ?? employeePct(p, salary);
  const employee = Math.min((salary * ep) / 100, tspLimit(age));
  const effPct = salary > 0 ? (employee / salary) * 100 : 0;
  const agency = (salary * matchPct(p.retirement_system, effPct)) / 100;
  const ira = Math.min((Number(p.ira_trad_annual) || 0) + (Number(p.ira_roth_annual) || 0), iraLimit(age));
  return { employee, agency, ira, total: employee + agency + ira, employeePct: effPct };
}

export function goalTarget(p) {
  return p.goal_mode === "income" ? (Number(p.target_monthly_income) * 12) / (Number(p.withdrawal_rate) / 100) : Number(p.target_nest_egg);
}

export function project(p, startBalance, opts = {}) {
  const retireAge = opts.retireAge ?? p.retire_age;
  const r = (opts.returnRate ?? blendedReturn(p.allocation)) / 100;
  const growth = ((Number(p.salary_growth) || 0) + (Number(p.step_increase_pct) || 0)) / 100;
  const years = Math.max(0, retireAge - p.age);
  const series = [];
  let bal = { conservative: startBalance, expected: startBalance, optimistic: startBalance };
  let salary = Number(p.salary) || 0;
  let contributed = startBalance;
  const year0 = new Date().getFullYear();
  series.push({ age: p.age, year: year0, ...bal, contributed });
  const cp = opts.contrib ? withContrib(p, opts.contrib) : p;
  for (let i = 1; i <= years; i++) {
    const c = annualContribs(cp, salary, p.age + i - 1, opts.contribPct).total;
    contributed += c;
    bal = {
      conservative: bal.conservative * (1 + r - 0.02) + c,
      expected: bal.expected * (1 + r) + c,
      optimistic: bal.optimistic * (1 + r + 0.02) + c,
    };
    series.push({ age: p.age + i, year: year0 + i, ...Object.fromEntries(Object.entries(bal).map(([k, v]) => [k, Math.round(v)])), contributed: Math.round(contributed) });
    salary *= 1 + growth;
  }
  const last = series[series.length - 1];
  return { series, final: last, rate: r * 100, years };
}

export function requiredMonthly(target, pv, ratePct, years) {
  if (years <= 0) return Math.max(0, target - pv);
  const rm = ratePct / 100 / 12;
  const n = years * 12;
  const fvpv = pv * Math.pow(1 + rm, n);
  if (rm === 0) return Math.max(0, (target - pv) / n);
  return Math.max(0, ((target - fvpv) * rm) / (Math.pow(1 + rm, n) - 1));
}

export function taxDrag(finalBal, p, balances) {
  const tsp = balances?.tsp || 0, trad = balances?.trad_ira || 0, roth = balances?.roth_ira || 0, other = balances?.other || 0;
  const total = tsp + trad + roth + other;
  const rothShare = (Number(p.roth_split) || 0) / 100;
  const tradFrac = total > 0 ? (tsp * (1 - rothShare) + trad) / total : 1 - rothShare;
  return finalBal * tradFrac * ((Number(p.tax_rate) || 0) / 100);
}

export const UNIFORM_TABLE = { 72: 27.4, 73: 26.5, 74: 25.5, 75: 24.6, 76: 23.7, 77: 22.9, 78: 22.0, 79: 21.1, 80: 20.2, 81: 19.4, 82: 18.5, 83: 17.7, 84: 16.8, 85: 16.0, 86: 15.2, 87: 14.4, 88: 13.7, 89: 12.9, 90: 12.2, 91: 11.5, 92: 10.8, 93: 10.1, 94: 9.5, 95: 8.9, 96: 8.4, 97: 7.8, 98: 7.3, 99: 6.8, 100: 6.4, 101: 6.0, 102: 5.6, 103: 5.2, 104: 4.9, 105: 4.6 };

export function rmdStartAge(birthYear) {
  if (birthYear <= 1950) return 72;
  if (birthYear <= 1959) return 73;
  return 75;
}

export function rmdSchedule(balanceAtStart, startAge, endAge, ratePct) {
  const rows = [];
  let bal = balanceAtStart;
  for (let a = startAge; a <= Math.min(endAge, 105); a++) {
    const div = UNIFORM_TABLE[a] || 4.6;
    const rmd = bal / div;
    rows.push({ age: a, balance: Math.round(bal), divisor: div, rmd: Math.round(rmd) });
    bal = (bal - rmd) * (1 + ratePct / 100);
  }
  return rows;
}

export function growTo(pv, annual, ratePct, years) {
  let b = pv;
  for (let i = 0; i < years; i++) b = b * (1 + ratePct / 100) + annual;
  return b;
}
